import React from 'react';
import { Card } from 'antd';
import { useLocation } from 'react-router-dom';
import { SkBar } from './_primitives';

const TAB_ORDER = ['items', 'categories', 'modifiers', 'group_modifiers', 'employees_meals'];

/** Normalize tab param — accept both dash and underscore variants. */
const normalizeTab = (raw) => (raw ? raw.replace(/-/g, '_') : 'items');

const CatalogHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-6 h-6 rounded bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-7" w="w-48" className="max-w-full" />
    </div>
    <SkBar h="h-9" w="w-28" className="rounded-md flex-shrink-0" />
  </div>
);

const TabsRow = ({ active = 0 }) => (
  <div className="flex gap-4 sm:gap-6 border-b border-gray-200 mb-4 overflow-x-auto">
    {TAB_ORDER.map((_, i) => (
      <div
        key={i}
        className={`pb-2 flex-shrink-0 ${active === i ? 'border-b-2 border-gray-300' : ''}`}
      >
        <SkBar h="h-4" w="w-24" />
      </div>
    ))}
  </div>
);

/* ─────────── Items tab ─────────── */

const ItemsFilterRow = () => (
  <div className="flex flex-col lg:flex-row gap-3 mb-4 items-stretch">
    <SkBar h="h-10" w="w-full lg:flex-1 lg:max-w-xs" className="rounded-md" />
    <SkBar h="h-10" w="w-full lg:w-48" className="rounded-md" />
    <SkBar h="h-10" w="w-full lg:w-48" className="rounded-md" />
    <div className="flex gap-2 flex-shrink-0">
      <SkBar h="h-10" w="w-full lg:w-28" className="rounded-md" />
      <SkBar h="h-10" w="w-full lg:w-28" className="rounded-md" />
    </div>
    <SkBar h="h-10" w="w-full lg:w-24" className="rounded-md flex-shrink-0" />
  </div>
);

export const ProductCardSkeleton = () => (
  <Card className="overflow-hidden" styles={{ body: { padding: 0 } }}>
    <div className="relative h-40 bg-gray-100">
      <div className="absolute top-2 left-2">
        <SkBar h="h-5" w="w-16" className="rounded-md bg-gray-300" />
      </div>
      <div className="absolute top-2 right-2">
        <SkBar h="h-5" w="w-16" className="rounded-md bg-gray-300" />
      </div>
      <div className="absolute top-10 left-2">
        <SkBar h="h-4" w="w-14" className="rounded-md bg-gray-300" />
      </div>
      <div className="absolute bottom-2 left-3 flex items-center gap-2">
        <SkBar h="h-4" w="w-16" className="bg-gray-300" />
        <div className="w-2 h-2 rounded-full bg-gray-300" />
      </div>
    </div>
    <div className="p-3 flex flex-col gap-3">
      <SkBar h="h-4" w="w-3/4" />
      <div className="flex flex-col gap-1.5">
        <SkBar h="h-3" w="w-full" />
        <SkBar h="h-3" w="w-2/3" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SkBar h="h-5" w="w-16" className="rounded-md" />
        <SkBar h="h-5" w="w-20" className="rounded-md" />
        <SkBar h="h-5" w="w-10" className="rounded-md" />
      </div>
      <SkBar h="h-8" w="w-full" className="rounded-md" />
      <SkBar h="h-8" w="w-full" className="rounded-md" />
      <div className="flex items-center justify-between">
        <SkBar h="h-3" w="w-16" />
        <SkBar h="h-5" w="w-10" className="rounded-full" />
      </div>
      <div className="flex items-center justify-between">
        <SkBar h="h-3" w="w-14" />
        <SkBar h="h-5" w="w-10" className="rounded-full" />
      </div>
    </div>
  </Card>
);

const ItemsTabContent = () => (
  <>
    <ItemsFilterRow />
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
      {Array.from({ length: 15 }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  </>
);

/* ─────────── Categories tab ─────────── */

const CategoriesFilterRow = () => (
  <div className="flex flex-col sm:flex-row gap-3 mb-4">
    <SkBar h="h-10" w="w-full sm:flex-1 sm:max-w-md" className="rounded-md" />
    <SkBar h="h-10" w="w-full sm:w-24" className="rounded-md flex-shrink-0" />
  </div>
);

const CATEGORY_COLS = ['', 'w-28', 'w-28', 'w-24', 'w-12', 'w-14', 'w-24', ''];

const CategoryRow = () => (
  <div className="grid grid-cols-[0.6fr_2fr_1.5fr_1fr_1fr_1fr_1.2fr_0.8fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Image */}
    <div className="w-12 h-12 rounded-lg bg-gray-200 animate-pulse" />
    {/* Category Name + (Custom) */}
    <div className="flex items-center gap-2 min-w-0">
      <SkBar h="h-3" w="w-28" />
      <SkBar h="h-3" w="w-14" />
    </div>
    {/* Alternate Name */}
    <SkBar h="h-3" w="w-28" />
    {/* Sub-Categories */}
    <div className="flex flex-col items-center gap-1">
      <SkBar h="h-5" w="w-6" />
      <SkBar h="h-2" w="w-20" />
    </div>
    {/* Items */}
    <div className="flex flex-col items-center gap-1">
      <SkBar h="h-5" w="w-6" />
      <SkBar h="h-2" w="w-10" />
    </div>
    {/* Status switch */}
    <SkBar h="h-6" w="w-16" className="rounded-full justify-self-center" />
    {/* Last Updated */}
    <div className="flex flex-col gap-1">
      <SkBar h="h-3" w="w-20" />
      <SkBar h="h-2" w="w-14" />
    </div>
    {/* View button */}
    <SkBar h="h-8" w="w-16" className="rounded-md justify-self-end" />
  </div>
);

const CategoriesTablePagination = () => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mt-4 pt-3">
    <SkBar h="h-3" w="w-40" />
    <div className="flex items-center gap-2">
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
    </div>
    <SkBar h="h-8" w="w-24" className="rounded-md" />
  </div>
);

const CategoriesTabContent = () => (
  <>
    <CategoriesFilterRow />
    <div className="overflow-x-auto">
      <div className="min-w-[1000px]">
        <div className="grid grid-cols-[0.6fr_2fr_1.5fr_1fr_1fr_1fr_1.2fr_0.8fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 rounded-t">
          {CATEGORY_COLS.map((w, i) => (
            <SkBar
              key={i}
              h="h-3"
              w={w || 'w-0'}
              className={
                i === CATEGORY_COLS.length - 1
                  ? 'justify-self-end'
                  : i === 0
                  ? ''
                  : [3, 4, 5].includes(i)
                  ? 'justify-self-center'
                  : ''
              }
            />
          ))}
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <CategoryRow key={i} />
        ))}
      </div>
    </div>
    <CategoriesTablePagination />
  </>
);

/* ─────────── Group Modifiers tab ─────────── */

const GROUP_MOD_COLS = ['w-16', 'w-16', 'w-28', 'w-28', 'w-20', 'w-24', 'w-14', ''];

const GroupModifierRow = () => (
  <div className="grid grid-cols-[0.8fr_2fr_1.8fr_1.2fr_1fr_1fr_1fr_0.8fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Marn ID pill */}
    <SkBar h="h-6" w="w-12" className="rounded-md" />
    {/* Name + (Custom) */}
    <div className="flex items-center gap-2 min-w-0">
      <SkBar h="h-3" w="w-28" />
      <SkBar h="h-3" w="w-14" />
    </div>
    {/* Alternate Name */}
    <SkBar h="h-3" w="w-24" />
    {/* Selection Range */}
    <SkBar h="h-4" w="w-10" className="justify-self-center" />
    {/* Required pill */}
    <SkBar h="h-6" w="w-10" className="rounded-md justify-self-center" />
    {/* Single Select pill */}
    <SkBar h="h-6" w="w-10" className="rounded-md justify-self-center" />
    {/* Status pill */}
    <SkBar h="h-6" w="w-16" className="rounded-md justify-self-center" />
    {/* View button */}
    <SkBar h="h-8" w="w-16" className="rounded-md justify-self-end" />
  </div>
);

const GroupModifiersTabContent = () => (
  <>
    <CategoriesFilterRow />
    <div className="overflow-x-auto">
      <div className="min-w-[1000px]">
        <div className="grid grid-cols-[0.8fr_2fr_1.8fr_1.2fr_1fr_1fr_1fr_0.8fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
          {GROUP_MOD_COLS.map((w, i) => (
            <SkBar
              key={i}
              h="h-3"
              w={w || 'w-0'}
              className={[3, 4, 5, 6].includes(i) ? 'justify-self-center' : ''}
            />
          ))}
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <GroupModifierRow key={i} />
        ))}
      </div>
    </div>
    <CategoriesTablePagination />
  </>
);

/* ─────────── Employees Meals tab ─────────── */

const EmployeesMealsRow = () => (
  <div className="grid grid-cols-[0.8fr_3fr_3fr_0.8fr] gap-4 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Per Day Limit: big number + subtitle */}
    <div className="flex flex-col items-start gap-1">
      <SkBar h="h-6" w="w-8" />
      <SkBar h="h-2" w="w-16" />
    </div>
    {/* Allowed Employees pills */}
    <div className="flex flex-wrap items-center gap-2">
      <SkBar h="h-6" w="w-40" className="rounded-md" />
      <SkBar h="h-6" w="w-32" className="rounded-md" />
      <SkBar h="h-6" w="w-44" className="rounded-md" />
      <SkBar h="h-5" w="w-14" />
    </div>
    {/* Allowed Roles pills */}
    <div className="flex flex-wrap items-center gap-2">
      <SkBar h="h-6" w="w-24" className="rounded-md" />
      <SkBar h="h-6" w="w-28" className="rounded-md" />
      <SkBar h="h-6" w="w-32" className="rounded-md" />
      <SkBar h="h-5" w="w-14" />
    </div>
    {/* Actions column */}
    <SkBar h="h-8" w="w-16" className="rounded-md justify-self-end" />
  </div>
);

const EmployeesMealsTabContent = () => (
  <>
    <CategoriesFilterRow />
    <div className="overflow-x-auto">
      <div className="min-w-[1100px]">
        <div className="grid grid-cols-[0.8fr_3fr_3fr_0.8fr] gap-4 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
          <SkBar h="h-3" w="w-20" />
          <SkBar h="h-3" w="w-28" />
          <SkBar h="h-3" w="w-24" />
          <SkBar h="h-3" w="w-14" className="justify-self-end" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <EmployeesMealsRow key={i} />
        ))}
      </div>
    </div>
    <CategoriesTablePagination />
  </>
);

/* ─────────── Generic list tab (Modifiers only) ─────────── */

const GenericListTabContent = () => (
  <>
    <CategoriesFilterRow />
    <div className="overflow-x-auto">
      <div className="min-w-[900px]">
        <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_1fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 rounded-t py-2">
          {['w-24', 'w-24', 'w-16', 'w-16', 'w-16', 'w-20'].map((w, i) => (
            <SkBar key={i} h="h-3" w={w} />
          ))}
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_1fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
              <SkBar h="h-3" w="w-32" />
            </div>
            <SkBar h="h-3" w="w-28" />
            <SkBar h="h-3" w="w-12" />
            <SkBar h="h-3" w="w-12" />
            <SkBar h="h-6" w="w-16" className="rounded-full" />
            <SkBar h="h-8" w="w-16" className="rounded-md justify-self-end" />
          </div>
        ))}
      </div>
    </div>
    <CategoriesTablePagination />
  </>
);

/* ─────────── Main loader ─────────── */

const AdminMenuPageLoader = () => {
  const { search } = useLocation();
  const tab = normalizeTab(new URLSearchParams(search).get('tab'));
  const activeIndex = Math.max(0, TAB_ORDER.indexOf(tab));

  let content;
  if (tab === 'categories') content = <CategoriesTabContent />;
  else if (tab === 'group_modifiers') content = <GroupModifiersTabContent />;
  else if (tab === 'employees_meals') content = <EmployeesMealsTabContent />;
  else if (tab === 'modifiers') content = <GenericListTabContent />;
  else content = <ItemsTabContent />;

  return (
    <div className="w-full p-3 sm:p-4 md:p-6">
      <CatalogHeader />
      <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
        <TabsRow active={activeIndex} />
        {content}
      </Card>
    </div>
  );
};

export default AdminMenuPageLoader;
